<template>
    <PageLoading v-if="isLoading" />
    <div v-else class="main-wrap wishlist-page">
        <h1>{{ $t('wishlist.wishlist') }}</h1>
        <div class="filter-wrap row q-col-gutter-md">
            <q-select
                v-model="sortBySelected"
                class="col-12 col-sm-6 col-sm-6 col-md-4 col-lg-3"
                square
                outlined
                dense
                clearable
                :options="sortBy"
                :label="$t('general.order_by')"
                @input="resetPagination"
            />
            <q-input
                v-model="search"
                class="col-12 col-sm-6 col-md-grow"
                :placeholder="$t('general.search_courses')"
                square
                outlined
                dense
                clearable
                debounce="1000"
                :input-style="{ color: 'grey' }"
                @input="resetPagination"
            >
            </q-input>
            <div class="col-12 col-md-shrink">
                <q-btn
                    class="search-btn"
                    icon="search"
                    color="primary"
                    unelevated
                    dense
                    @click="fetchWishlist()"
                ></q-btn>
            </div>
        </div>
        <div v-if="!isFetchingWishlist" class="courses-wrap">
            <q-inner-loading :showing="isLoading">
                <q-spinner-pie color="primary" size="40px" />
            </q-inner-loading>
            <CourseThumb
                v-for="(course, index) in wishlist"
                :key="index"
                :index="index"
                :course="course.wishlists_courses"
                @added="removeFromWishlist"
                @removed="removeFromWishlist"
            />
        </div>
        <h5 v-else>{{ $t('general.no_results') }}</h5>
        <div class="pagination-wrap">
            <q-pagination
                v-if="pagination.max != 0"
                v-model="pagination.page"
                text-color="grey-8"
                :max="pagination.max"
                :max-pages="6"
                :direction-links="true"
                :boundary-links="true"
                :boundary-numbers="true"
                @input="fetchWishlist"
            ></q-pagination>
        </div>
    </div>
</template>

<script>
import PageLoading from '@/components/PageLoading'
import CourseThumb from '@/components/CourseThumb'
import wishlistService from '@/services/modules/wishlist'
import store from '@/store'

const loadingKeys = {
    fetchingWishlist: 'fetching-wishlist',
    removingFromWishlist: 'removing-from-wishlist',
}
export default {
    name: 'Wishlist',

    components: {
        PageLoading,
        CourseThumb,
    },

    data() {
        return {
            wishlist: [],

            search: '',

            sortBySelected: null,

            sortBy: this.createOrderByOptions(),

            pagination: {
                max: 1,
                page: 1,
                rowsPerPage: 10,
                rowsNumber: 0,
            },
        }
    },

    computed: {
        isLoading() {
            return this.isFetchingWishlist || this.isRemovingFromWishlist
        },

        isFetchingWishlist() {
            return !!this.loadings.filter(
                (loading) => loading.key === loadingKeys.fetchingWishlist
            ).length
        },

        isRemovingFromWishlist() {
            return !!this.loadings.filter(
                (loading) => loading.key === loadingKeys.removingFromWishlist
            ).length
        },

        user() {
            return store.getters['session/user']
        },

        locale() {
            return this.$store.getters['locale/currentLocale']
        },
    },

    watch: {
        locale() {
            this.sortBy = this.createOrderByOptions()

            if (this.sortBySelected) {
                let selected = this.sortBy.find((option) => {
                    return option.id == this.sortBySelected.id
                })

                this.sortBySelected = selected
            }

            // Allows to order by course name (A-Z / Z-A)
            this.fetchWishlist()
        },
    },

    async created() {
        await this.fetchWishlist()
    },

    methods: {
        createOrderByOptions() {
            return [
                {
                    id: 1,
                    label: this.$t('general.more_recents'),
                    column: 'wishlists.created_at',
                    direction: 'desc',
                },
                {
                    id: 2,
                    label: this.$t('general.older'),
                    column: 'wishlists.created_at',
                    direction: 'asc',
                },
                {
                    id: 3,
                    label: this.$t('general.course_name_asc'),
                    column: 'wishlists_courses.title',
                    direction: 'asc',
                },
                {
                    id: 4,
                    label: this.$t('general.course_name_desc'),
                    column: 'wishlists_courses.title',
                    direction: 'desc',
                },
            ]
        },

        resetPagination() {
            this.pagination.page = 1
            this.fetchWishlist()
        },

        constructFilter() {
            return {
                user_id: this.user.id,
                search: this.search,
            }
        },

        constructOrderBy() {
            let orderBy = this.sortBySelected
                ? {
                      column: this.sortBySelected.column,
                      direction: this.sortBySelected.direction,
                  }
                : { column: 'created_at', direction: 'desc' }
            return orderBy
        },

        constructPagination() {
            return {
                perPage: this.pagination.rowsPerPage,
                currentPage: this.pagination.page,
            }
        },

        async fetchWishlist() {
            this.loadingAdd(loadingKeys.fetchingWishlist)

            let parameters = {
                filter: this.constructFilter(),
                orderBy: [this.constructOrderBy()],
                pagination: this.constructPagination(),
                relations: {
                    courses: true,
                },
            }

            let response = await wishlistService.retrieve(parameters)

            if (response.status === 200) {
                response.data.results.forEach((course) => {
                    course.wishlists_courses.wishlist_date = course.created_at
                })
                this.wishlist = response.data.results
                this.wishlist.sort()

                let total = response.data.pagination.total
                let pages = total / this.pagination.rowsPerPage
                this.pagination.max = Math.ceil(pages)

                // update wishlist store count
                store.dispatch(
                    'wishlist/updateCount',
                    response.data.pagination.total
                )
            }

            this.loadingRemove(loadingKeys.fetchingWishlist)
        },

        async removeFromWishlist(courseId) {
            this.loadingAdd(loadingKeys.removingFromWishlist)

            let parameters = {
                data: [
                    {
                        course_id: courseId,
                        user_id: this.user.id,
                    },
                ],
            }
            let response = await wishlistService.delete(parameters)

            if (response.status !== 200) {
                this.$q.notify({
                    type: 'negative',
                    message: this.$t('notify.error_occurred', {
                        message: response.data.message,
                    }),
                })
            } else {
                this.fetchWishlist()
                this.$q.notify({
                    type: 'positive',
                    message: this.$t('notify.course_removed_from_wishlist'),
                })
            }

            this.loadingRemove(loadingKeys.removingFromWishlist)
        },
    },
}
</script>
